import React, {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { colors } from '@alixpartners/ui-components';
import { CHART_COLORS, FONTSTYLE } from './utils';
import { getClientVSDiversedSpend } from 'utils/apiRequests';
import { renderToString } from 'react-dom/server';
import {
	TooltipHeader,
	TooltipRowPinned,
	TooltipRowTitle,
	TooltipRowValue,
	TooltipWrapper,
} from './SupplierDiversity.styles';
import { getFormattedSpendWithSymbol } from 'utils/number';
import { QueryStringContext } from 'contexts/QueryStringContext';

const DiversedSpend = createContext();

export const DiversedSpendProvider = ({
	children,
	currencySymbol,
	hideDecimals,
}) => {
	const { currency } = useContext(QueryStringContext);
	const { dateFrom, dateTo } = useContext(QueryStringContext);

	const dateRange = useMemo(
		() => ({
			dateFrom: dateFrom.split('T')[0],
			dateTo: dateTo.split('T')[0],
		}),
		[dateFrom, dateTo]
	);
	const intialChartOptions = {
		chart: {
			type: 'column',
			style: { fontFamily: 'Roboto' },
			marginTop: 20,
		},
		exporting: { enabled: false, allowHTML: true },
		title: { text: '', align: 'left' },
		credits: { enabled: false },
		xAxis: {
			categories: [],
			title: { text: '', style: { ...FONTSTYLE } },
			lineColor: colors.black,
			labels: { style: { ...FONTSTYLE } },
			gridLineWidth: 0,
		},
		yAxis: {
			allowDecimals: false,
			title: {
				text: '',
			},
		},
		plotOptions: {
			column: {
				borderRadius: 0,
				stacking: 'percent',
			},
		},
		tooltip: {
			useHTML: true,
			shared: true,
			backgroundColor: colors.white,
			borderWidth: 0,
			outside: true,
			borderRadius: 2,
			formatter: function() {
				const { points, x, y } = this;
				if (points.length === 2) {
					const diversityPercentage = (points[0].y / points[1].y) * 100;
					const clientSpend = points[1].y - points[0].y;
					return renderToString(
						<TooltipWrapper>
							<TooltipHeader>{x}</TooltipHeader>
							<TooltipRowPinned>
								<div />
								<TooltipRowTitle>{points[0].series.name}</TooltipRowTitle>
								<TooltipRowValue>
									{`${getFormattedSpendWithSymbol(
										points[0].y,
										currencySymbol,
										hideDecimals
									).toUpperCase()} (${diversityPercentage.toFixed(2)}%)`}
								</TooltipRowValue>
							</TooltipRowPinned>
							<TooltipRowPinned>
								<div />
								<TooltipRowTitle>{points[1].series.name}</TooltipRowTitle>
								<TooltipRowValue>
									{`${getFormattedSpendWithSymbol(
										clientSpend,
										currencySymbol,
										hideDecimals
									).toUpperCase()} (${(100 - diversityPercentage).toFixed(
										2
									)} %)`}
								</TooltipRowValue>
							</TooltipRowPinned>
						</TooltipWrapper>
					);
				} else {
					return renderToString(
						<TooltipWrapper>
							<TooltipHeader>{x}</TooltipHeader>
							<TooltipRowPinned>
								<div />
								<TooltipRowTitle>{points[0].series.name}</TooltipRowTitle>
								<TooltipRowValue>
									{getFormattedSpendWithSymbol(
										y,
										currencySymbol,
										hideDecimals
									).toUpperCase()}
								</TooltipRowValue>
							</TooltipRowPinned>
						</TooltipWrapper>
					);
				}
			},
		},
		series: [],
	};
	const [isLoading, setIsLoading] = useState(false);
	const [data, setData] = useState([]);
	const [options, setOptions] = useState(intialChartOptions);

	useEffect(
		() => {
			getData();
		}, // eslint-disable-next-line
		[dateRange]
	);

	const getData = () => {
		setIsLoading(true);
		getClientVSDiversedSpend(dateRange)
			.then(response => {
				if (response.succeeded) {
					const dataWithZero = response.data.filter(
						x => x.total_Diversified_Spend === 0
					);
					if (response.data.length !== dataWithZero.length) {
						setData(response.data);
						const sortedData = response.data.sort(
							(a, b) => b.total_Spend - a.total_Spend
						);
						const categoriesData = sortedData.map(x => x.aP_L1Category);
						const clienSpendData = sortedData.map(x => x.total_Spend);
						const diversedSpendData = sortedData.map(
							x => x.total_Diversified_Spend
						);

						setOptions(prevState => ({
							...prevState,
							xAxis: {
								...prevState.xAxis,
								categories: categoriesData,
							},
							series: [
								{
									name: 'Diversity Spend',
									data: diversedSpendData,
									color: CHART_COLORS[1],
								},
								{
									name: 'Client Spend',
									data: clienSpendData,
									color: CHART_COLORS[0],
								},
							],
						}));
					}
				}
			})
			.catch(() => setIsLoading(false))
			.finally(() => setIsLoading(false));
	};

	return (
		<DiversedSpend.Provider
			value={{
				isLoading,
				data,
				options,
			}}
		>
			{children}
		</DiversedSpend.Provider>
	);
};

export const useDiversedSpendContext = () => {
	return useContext(DiversedSpend);
};
